import { InvestmentRoundingSetting, SafeCapType } from 'shared';

import { StringKey } from '../lang/index';
import { Valuation } from './events.types';
import { ShareClass } from './share-classes.types';
import { Stakeholder } from './stakeholder.types';

export type SafeConversion = {
  additionalNotes: string;
  conversionDate: Date;
  convertedShares: number;
  createdAt: Date;
  filesLinks: string[];
  id: string;
  items: SafeWithPermissions[];
  postConversionSharePrice: number;
  preConversionSharePrice: number;
  roundingSetting: InvestmentRoundingSetting;
  shareClass: ShareClass;
  valuation: Valuation;
};

export type SafeWithPermissions = { canDelete: boolean; canEdit: boolean } & Safe;

export type Safe = {
  additionalNotes?: string;
  capitalOwned: number;
  capType?: null | SafeCapType;
  capValue?: number;
  createdAt: Date;
  discount: number;
  discountOwnership: number;
  docusignFileLinks?: string[];
  filesLinks: string[];
  floorOwnership: number;
  floorValue: number;
  id: string;
  investment: number;
  issueDate: Date;
  maturityDate?: Date;
  stakeholder: Stakeholder;
  valuationCapOwnership: number;
} & OR<
  {
    conversion?: SafeConversion;
    conversionDate: Date;
    conversionMethod?: ConversionMethod;
    conversionSharePrice?: null | number;
    convertedShares: number;
    isConvertedToShares: true;
  },
  { isConvertedToShares: false }
>;

export enum ConversionMethod {
  DISCOUNT = 'discount',
  FLOOR = 'floor',
  VALUATION_CAP = 'valuation-cap',
}

export const conversionMethodTitle: Record<ConversionMethod, StringKey> = {
  [ConversionMethod.DISCOUNT]: 'DISCOUNT',
  [ConversionMethod.FLOOR]: 'FLOOR',
  [ConversionMethod.VALUATION_CAP]: 'VALUATION_CAP',
};

export const safeCapTypeTitle: Record<SafeCapType, StringKey> = {
  [SafeCapType.POST_MONEY]: 'POST_MONEY',
  [SafeCapType.PRE_MONEY]: 'PRE_MONEY',
};

/** `Pre-Money Cap` — the two options in the Add / Edit SAFE cap type picker. */
export const safeCapTypeOptionTitle: Record<SafeCapType, StringKey> = {
  [SafeCapType.POST_MONEY]: 'POST_MONEY_CAP',
  [SafeCapType.PRE_MONEY]: 'PRE_MONEY_CAP',
};

/**
 * Order cap type choices are offered in, pre-money first, as the Add SAFE design specifies.
 * The enum's own member order is alphabetical and says nothing about presentation.
 */
export const safeCapTypeOptions = [SafeCapType.PRE_MONEY, SafeCapType.POST_MONEY] as const;

/** `Cap (Pre-Money)` — Add SAFE review step and the View / Edit SAFE preview. */
export const safeCapTypeValuationCapTitle: Record<SafeCapType, StringKey> = {
  [SafeCapType.POST_MONEY]: 'CAP_POST_MONEY',
  [SafeCapType.PRE_MONEY]: 'CAP_PRE_MONEY',
};

/** `Valuation Cap (Pre-Money)` — conversion method of a converted SAFE. */
export const conversionMethodWithCapTypeTitle: Record<SafeCapType, StringKey> = {
  [SafeCapType.POST_MONEY]: 'VALUATION_CAP_POST_MONEY',
  [SafeCapType.PRE_MONEY]: 'VALUATION_CAP_PRE_MONEY',
};

/**
 * Converted SAFEs name the cap type in brackets next to a cap conversion (story 13.13 §5/§6);
 * discount and floor conversions never had a cap type to show.
 */
export const getConversionMethodTitle = (
  conversionMethod: ConversionMethod,
  capType?: null | SafeCapType,
): StringKey =>
  conversionMethod === ConversionMethod.VALUATION_CAP && capType
    ? conversionMethodWithCapTypeTitle[capType]
    : conversionMethodTitle[conversionMethod];

export enum DetailsTab {
  CONVERTED = 'converted',
  PENDING_CONVERSION = 'pending-conversion',
}

export const detailsTabTitle: Record<DetailsTab, StringKey> = {
  [DetailsTab.PENDING_CONVERSION]: 'PENDING_CONVERSION',
  [DetailsTab.CONVERTED]: 'CONVERTED',
};

export const detailsTabTitleMap = Object.entries(detailsTabTitle);

export enum SafeOrder {
  ASC = 'asc',
  DESC = 'desc',
}

export enum SafeSortBy {
  CAP_TYPE = 'cap-type',
  CAP_VALUE = 'cap-value',
  CONVERSION_DATE = 'conversion-date',
  CONVERSION_METHOD = 'conversion-method',
  CONVERTED_SHARES = 'converted-shares',
  DISCOUNT = 'discount',
  FLOOR = 'floor',
  FULL_NAME = 'full-name',
  ISSUE_DATE = 'issue-date',
  MATURITY_DATE = 'maturity-date',
}

export const itemsToFetchMap = [5, 10, 20, 50, 100];

export type CreateSafeDto = {
  additionalNotes?: string;
  capType?: SafeCapType;
  capValue?: number;
  discount?: number;
  filesLinks: string[];
  floorValue?: number;
  investment: number;
  issueDate: string;
  maturityDate?: string;
  stakeholderId: string;
};

export type PatchSafeDto = {
  additionalNotes?: string;
  capType?: null | SafeCapType;
  capValue?: null | number;
  discount?: null | number;
  filesLinks?: string[];
  floorValue?: null | number;
  investment?: number;
  issueDate?: string;
  maturityDate?: null | string;
  stakeholderId?: string;
};

export type ConvertToSharesSummaryDto = {
  roundingSetting?: InvestmentRoundingSetting;
  safeIds: string[];
  valuationId?: string;
};

export type ConvertSafesDto = {
  additionalNotes?: string;
  conversionDate: string;
  filesLinks: string[];
  roundingSetting?: InvestmentRoundingSetting;
  safes: { filesLinks: string[]; id: string }[];
  shareClassId: string;
  valuationId?: string;
};
